// Property Detail page with sub-tabs backed by real API
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageHeader from '../../components/shared/PageHeader';
import { Card, StatusPill } from '../../components/shared/DataToolbar';
import { LoadingState, ErrorState } from '../../components/shared/Pending';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useApi } from '../../api/useApi';
import * as api from '../../api/hostApi';
import { toast } from 'sonner';
import { ArrowLeft, Edit2, Star, Home, MapPin, Users, BedDouble, Bath, Clock, Plus, Trash2, Upload, Image as ImageIcon, Loader2 } from 'lucide-react';

export default function PropertyDetail() {
  const { id } = useParams();
  const { data: p, loading, error, refetch } = useApi(() => api.getProperty(id), [id]);
  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  if (!p) return null;

  return (
    <div>
      <PageHeader
        breadcrumbs={['Properties', p.title]}
        title={p.title}
        description={p.subtitle || `${p.city || ''}${p.state ? ', ' + p.state : ''}`}
        actions={
          <>
            <Button asChild variant="outline" className="h-10"><Link to="/host/properties"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Link></Button>
            <Button asChild className="bg-teal-700 hover:bg-teal-800 h-10"><Link to={`/host/properties/${p.id}/edit`}><Edit2 className="h-4 w-4 mr-1" /> Edit basics</Link></Button>
          </>
        }
      />
      <Tabs defaultValue="overview">
        <TabsList className="bg-white border border-slate-200 rounded-lg p-1 flex-wrap h-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="images">Images</TabsTrigger>
          <TabsTrigger value="features">Features</TabsTrigger>
          <TabsTrigger value="rules">Rules</TabsTrigger>
          <TabsTrigger value="policies">Policies</TabsTrigger>
          <TabsTrigger value="faqs">FAQs</TabsTrigger>
          <TabsTrigger value="availability">Availability</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4"><OverviewTab p={p} /></TabsContent>
        <TabsContent value="images" className="mt-4"><ImagesTab p={p} /></TabsContent>
        <TabsContent value="features" className="mt-4"><FeaturesTab p={p} /></TabsContent>
        <TabsContent value="rules" className="mt-4"><RulesTab p={p} /></TabsContent>
        <TabsContent value="policies" className="mt-4"><PoliciesTab p={p} /></TabsContent>
        <TabsContent value="faqs" className="mt-4"><FaqsTab p={p} /></TabsContent>
        <TabsContent value="availability" className="mt-4"><AvailabilityTab p={p} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <div className="flex items-center gap-2 text-slate-500"><Icon className="h-4 w-4" /> {label}</div>
      <div className="font-medium text-slate-900">{value ?? '—'}</div>
    </div>
  );
}

function OverviewTab({ p }) {
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="lg:col-span-2 overflow-hidden">
        {(p.cover_image || p.coverImage) && <div className="aspect-[16/8] overflow-hidden"><img src={p.cover_image || p.coverImage} className="h-full w-full object-cover" alt="" /></div>}
        <div className="p-6">
          <div className="flex items-center gap-2 flex-wrap"><StatusPill status={p.status} />{p.featured && <span className="px-2 py-0.5 rounded-full bg-amber-400 text-amber-900 text-[10px] font-bold uppercase tracking-wider">Featured</span>}</div>
          <h2 className="mt-3 text-xl font-bold text-slate-900">{p.title}</h2>
          <div className="mt-2 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-4 w-4" /> {p.address || `${p.city || ''}${p.state ? ', ' + p.state : ''}`}</div>
          {p.description && <p className="mt-4 text-sm text-slate-700 whitespace-pre-line leading-relaxed">{p.description}</p>}
        </div>
      </Card>
      <Card className="p-5">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Snapshot</div>
        <div className="mt-3 space-y-2 text-sm">
          <Row icon={Home} label="Price / night" value={`$${p.price_per_night ?? p.pricePerNight}`} />
          <Row icon={Users} label="Max guests" value={p.max_guests ?? p.maxGuests} />
          <Row icon={BedDouble} label="Bedrooms" value={p.bedrooms} />
          <Row icon={Bath} label="Bathrooms" value={p.bathrooms} />
          <Row icon={Clock} label="Check-in" value={p.check_in_time || p.checkInTime} />
          <Row icon={Clock} label="Check-out" value={p.check_out_time || p.checkOutTime} />
          <Row icon={Star} label="Rating" value={`${p.rating || '—'} (${p.reviews_count ?? p.reviewsCount ?? 0})`} />
        </div>
      </Card>
    </div>
  );
}

function ImagesTab({ p }) {
  const { data, loading, error, refetch } = useApi(() => api.listPropertyImages(p.id), [p.id]);
  const images = useMemo(() => Array.isArray(data) ? data : (data?.items || []), [data]);
  const [file, setFile] = useState(null);
  const [alt, setAlt] = useState('');
  const [asCover, setAsCover] = useState(false);
  const [uploading, setUploading] = useState(false);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      if (alt) fd.append('alt_text', alt);
      if (asCover) fd.append('set_as_cover', '1');
      await api.uploadPropertyImages(p.id, fd);
      toast.success('Image uploaded'); setFile(null); setAlt(''); setAsCover(false); refetch();
    } catch (e) { toast.error(e?.message || 'Upload failed'); }
    finally { setUploading(false); }
  };
  const setCover = async (imgId) => { try { await api.setPropertyImageCover(p.id, imgId); toast.success('Cover updated'); refetch(); } catch (e) { toast.error(e?.message); } };
  const del = async (imgId) => { try { await api.deletePropertyImage(p.id, imgId); toast.success('Removed'); refetch(); } catch (e) { toast.error(e?.message); } };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  return (
    <div>
      <Card className="p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Upload image</h3>
        <div className="grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div><Label>Image file</Label><Input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0])} className="mt-1.5" /></div>
          <div><Label>Alt text (optional)</Label><Input value={alt} onChange={(e) => setAlt(e.target.value)} className="mt-1.5" /></div>
          <Button onClick={upload} disabled={!file || uploading} className="bg-teal-700 hover:bg-teal-800 h-10">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" /> Upload</>}</Button>
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm text-slate-600"><Checkbox checked={asCover} onCheckedChange={setAsCover} /> Set as cover</label>
      </Card>
      <Card className="p-6 mt-4">
        <div className="flex items-center justify-between mb-4"><h3 className="font-semibold text-slate-900">Gallery</h3><div className="text-xs text-slate-500">{images.length} image{images.length !== 1 && 's'}</div></div>
        {images.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-500"><ImageIcon className="h-8 w-8 mx-auto text-slate-300 mb-2" />No images yet.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((img) => (
              <div key={img.id} className="group rounded-lg overflow-hidden border border-slate-200 bg-white">
                <div className="aspect-[4/3] overflow-hidden bg-slate-100"><img src={img.url} alt={img.alt_text || img.alt || ''} className="h-full w-full object-cover" /></div>
                <div className="p-2 space-y-1.5">
                  {(img.is_cover || img.isCover) && <span className="text-[10px] font-bold uppercase tracking-wider text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded">Cover</span>}
                  <div className="flex items-center gap-2">
                    {!(img.is_cover || img.isCover) && <button className="flex-1 text-[11px] font-medium text-teal-700 border border-slate-200 rounded px-2 py-1 hover:bg-teal-50" onClick={() => setCover(img.id)}>Set cover</button>}
                    <button className="text-[11px] font-medium text-rose-600 border border-rose-200 rounded px-2 py-1 hover:bg-rose-50" onClick={() => del(img.id)}><Trash2 className="h-3 w-3" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function FeaturesTab({ p }) {
  const { data, loading, error, refetch } = useApi(() => api.listPropertyFeatures(p.id), [p.id]);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());
  useEffect(() => { if (data) { const sel = new Set((data.selected || []).map((f) => f.id || f)); setSelected(sel); } }, [data]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  const available = data?.available || data?.features || [];
  const toggle = (id, on) => { const s = new Set(selected); on ? s.add(id) : s.delete(id); setSelected(s); };
  const save = async () => { setSaving(true); try { await api.updatePropertyFeatures(p.id, Array.from(selected)); toast.success('Saved'); refetch(); } catch (e) { toast.error(e?.message); } finally { setSaving(false); } };

  return (
    <Card className="p-6">
      <h3 className="font-semibold text-slate-900">Features</h3>
      <p className="text-xs text-slate-500 mt-1">Select amenities shown on the property page.</p>
      <div className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {available.map((f) => {
          const on = selected.has(f.id);
          return (
            <label key={f.id} className={`flex items-center gap-2.5 rounded-lg border p-3 cursor-pointer ${on ? 'border-teal-500 bg-teal-50/60' : 'border-slate-200 hover:border-slate-300'}`}>
              <Checkbox checked={on} onCheckedChange={(v) => toggle(f.id, v)} />
              <span className="text-sm text-slate-900">{f.name}</span>
            </label>
          );
        })}
      </div>
      <div className="mt-6 flex justify-end"><Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}</Button></div>
    </Card>
  );
}

function RulesTab({ p }) {
  const { data, loading, error, refetch } = useApi(() => api.listPropertyRules(p.id), [p.id]);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());
  useEffect(() => { if (data) setSelected(new Set((data.selected || []).map((r) => r.id || r))); }, [data]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  const available = data?.available || data?.rules || [];
  const toggle = (id, on) => { const s = new Set(selected); on ? s.add(id) : s.delete(id); setSelected(s); };
  const save = async () => { setSaving(true); try { await api.updatePropertyRules(p.id, Array.from(selected)); toast.success('Saved'); refetch(); } catch (e) { toast.error(e?.message); } finally { setSaving(false); } };

  return (
    <Card className="p-6">
      <h3 className="font-semibold text-slate-900">House rules</h3>
      <div className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {available.map((r) => {
          const on = selected.has(r.id);
          return (
            <label key={r.id} className={`flex items-center gap-2.5 rounded-lg border p-3 cursor-pointer ${on ? 'border-teal-500 bg-teal-50/60' : 'border-slate-200 hover:border-slate-300'}`}>
              <Checkbox checked={on} onCheckedChange={(v) => toggle(r.id, v)} />
              <span className="text-sm text-slate-900">{r.name}</span>
            </label>
          );
        })}
      </div>
      <div className="mt-6 flex justify-end"><Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}</Button></div>
    </Card>
  );
}

function PoliciesTab({ p }) {
  const { data, loading, error, refetch } = useApi(() => api.listPropertyPolicies(p.id), [p.id]);
  const [saving, setSaving] = useState(false);
  const [choices, setChoices] = useState({}); // { policy_type_id: option_id }
  useEffect(() => {
    if (data) {
      const map = {};
      (data.selected || data.policies || []).forEach((pp) => { map[pp.policy_type_id || pp.typeId] = pp.policy_option_id || pp.optionId; });
      setChoices(map);
    }
  }, [data]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  const types = data?.types || data?.policy_types || [];
  const options = data?.options || data?.policy_options || [];
  const setChoice = (typeId, optionId) => setChoices((c) => ({ ...c, [typeId]: optionId }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.entries(choices).filter(([, v]) => !!v).map(([t, o]) => ({ policy_type_id: Number(t) || t, policy_option_id: Number(o) || o }));
      await api.updatePropertyPolicies(p.id, payload);
      toast.success('Policies updated'); refetch();
    } catch (e) { toast.error(e?.message); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-6">
      <h3 className="font-semibold text-slate-900">Policies</h3>
      <p className="text-xs text-slate-500 mt-1">Select one option per policy type.</p>
      <div className="mt-5 grid md:grid-cols-2 gap-5">
        {types.map((t) => (
          <div key={t.id} className="rounded-lg border border-slate-200 p-4">
            <div className="font-medium text-slate-900">{t.name}</div>
            {t.description && <div className="text-xs text-slate-500 mt-0.5">{t.description}</div>}
            <Select value={choices[t.id] ? String(choices[t.id]) : ''} onValueChange={(v) => setChoice(t.id, v)}>
              <SelectTrigger className="mt-3"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {options.filter((o) => (o.policy_type_id || o.typeId) === t.id).map((o) => <SelectItem key={o.id} value={String(o.id)}>{o.label || o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
      <div className="mt-6 flex justify-end"><Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}</Button></div>
    </Card>
  );
}

function FaqsTab({ p }) {
  const { data, loading, error, refetch } = useApi(() => api.listPropertyFaqs(p.id), [p.id]);
  const faqs = useMemo(() => Array.isArray(data) ? data : (data?.items || []), [data]);
  const [dialog, setDialog] = useState({ open: false, editing: null, form: { question: '', answer: '', order: 1, active: true } });

  const openNew = () => setDialog({ open: true, editing: null, form: { question: '', answer: '', order: faqs.length + 1, active: true } });
  const openEdit = (f) => setDialog({ open: true, editing: f.id, form: { question: f.question, answer: f.answer, order: f.order, active: !!f.active } });

  const submit = async () => {
    try {
      if (dialog.editing) await api.updatePropertyFaq(p.id, dialog.editing, dialog.form);
      else await api.createPropertyFaq(p.id, dialog.form);
      toast.success('Saved'); setDialog({ open: false, editing: null, form: { question: '', answer: '', order: 1, active: true } });
      refetch();
    } catch (e) { toast.error(e?.message); }
  };
  const del = async (f) => { try { await api.deletePropertyFaq(p.id, f.id); toast.success('Deleted'); refetch(); } catch (e) { toast.error(e?.message); } };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  return (
    <>
      <div className="flex justify-end mb-3"><Button onClick={openNew} className="bg-teal-700 hover:bg-teal-800 h-9"><Plus className="h-4 w-4 mr-1" /> Add FAQ</Button></div>
      <Card className="divide-y divide-slate-100">
        {faqs.length === 0 && <div className="p-10 text-center text-sm text-slate-500">No FAQs yet.</div>}
        {faqs.map(f => (
          <div key={f.id} className="p-4 flex items-start gap-3">
            <div className="h-6 w-6 rounded-full bg-slate-100 text-slate-600 text-xs font-bold grid place-items-center">{f.order}</div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-900">{f.question}</div>
              <div className="text-sm text-slate-600 mt-1">{f.answer}</div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => openEdit(f)}><Edit2 className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => del(f)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </Card>
      <Dialog open={dialog.open} onOpenChange={(o) => setDialog(d => ({ ...d, open: o }))}>
        <DialogContent>
          <DialogHeader><DialogTitle>{dialog.editing ? 'Edit FAQ' : 'Add FAQ'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Question</Label><Input value={dialog.form.question} onChange={(e) => setDialog(d => ({ ...d, form: { ...d.form, question: e.target.value } }))} className="mt-1.5" /></div>
            <div><Label>Answer</Label><Textarea rows={4} value={dialog.form.answer} onChange={(e) => setDialog(d => ({ ...d, form: { ...d.form, answer: e.target.value } }))} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Sort order</Label><Input type="number" value={dialog.form.order} onChange={(e) => setDialog(d => ({ ...d, form: { ...d.form, order: Number(e.target.value) } }))} className="mt-1.5" /></div>
              <div className="flex items-end"><label className="flex items-center gap-2 text-sm"><Switch checked={dialog.form.active} onCheckedChange={(v) => setDialog(d => ({ ...d, form: { ...d.form, active: v } }))} /> Active</label></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(d => ({ ...d, open: false }))}>Cancel</Button><Button onClick={submit} disabled={!dialog.form.question} className="bg-teal-700 hover:bg-teal-800">{dialog.editing ? 'Save' : 'Add'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AvailabilityTab({ p }) {
  const { data, loading, error, refetch } = useApi(() => api.listPropertyAvailability(p.id), [p.id]);
  const blocks = useMemo(() => Array.isArray(data) ? data : (data?.items || data?.blocks || []), [data]);
  const [form, setForm] = useState({ start_date: '', end_date: '', status: 'unavailable', notes: '' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.start_date || !form.end_date) return toast.error('Pick both dates');
    setSaving(true);
    try { await api.createAvailabilityBlock(p.id, form); toast.success('Block saved'); setForm({ start_date: '', end_date: '', status: 'unavailable', notes: '' }); refetch(); }
    catch (e) { toast.error(e?.message); } finally { setSaving(false); }
  };
  const del = async (b) => { try { await api.deleteAvailabilityBlock(p.id, b.id); toast.success('Removed'); refetch(); } catch (e) { toast.error(e?.message); } };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <h3 className="font-semibold text-slate-900 mb-3">Block dates</h3>
        <div className="grid md:grid-cols-4 gap-3 items-end">
          <div><Label>Start</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5" /></div>
          <div><Label>End</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="unavailable">Unavailable</SelectItem><SelectItem value="booked">Booked</SelectItem></SelectContent>
            </Select>
          </div>
          <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800 h-10">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save block'}</Button>
        </div>
        <div className="mt-2"><Label>Notes (optional)</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" placeholder="e.g. maintenance" /></div>
      </Card>
      <Card>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between"><h3 className="font-semibold text-slate-900">Existing blocks</h3><span className="text-xs text-slate-500">{blocks.length} total</span></div>
        {blocks.length === 0 ? <div className="p-10 text-center text-sm text-slate-500">No availability blocks yet.</div>
          : <div className="divide-y divide-slate-100">{blocks.map(b => (
            <div key={b.id} className="px-6 py-3 flex items-center gap-4">
              <div className="flex-1"><div className="text-sm font-medium text-slate-900">{b.start_date} → {b.end_date}</div><div className="text-xs text-slate-500">{b.notes}</div></div>
              <StatusPill status={b.status} />
              {b.locked ? <span className="text-xs text-slate-400">Locked</span> : <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => del(b)}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          ))}</div>}
      </Card>
    </div>
  );
}
